/**
 * Helper functions for units of measurement
 */

/**
 * Organizes units of measurement into optgroups by type
 * @param {Array} units - Array of unit objects with id, name, type, symbol, multiplicationFactor
 * @returns {Array} Array of optgroup objects with label and options
 */
export const organizeUnitsByType = (units) => {
	if (!units || !Array.isArray(units) || units.length === 0) {
		return [];
	}

	// Group units by type
	const groupedUnits = units.reduce((acc, unit) => {
		const type = unit.type || 'Other';
		if (!acc[type]) {
			acc[type] = [];
		}
		acc[type].push({
			value: unit.id,
			label: `${unit.symbol} (${unit.name})`
		});
		return acc;
	}, {});

	// Convert to optgroup format
	const optgroups = Object.entries(groupedUnits).map(([type, options]) => {
		return {
			label: getTypeDisplayName(type),
			options: options.sort((a, b) => { return a.label.localeCompare(b.label); })
		}; 
	});

	// Add "None" option at the beginning
	optgroups.unshift({
		label: 'None',
		options: [{ value: null, label: 'None' }]
	});

	return optgroups;
};

/**
 * Gets display name for unit type
 * @param {string} type - Unit type (storage, frequency, count, etc.)
 * @returns {string} Display name for the type
 */
const getTypeDisplayName = (type) => {
	const typeMap = {
		'storage': 'Storage Sizes',
		'frequency': 'Frequencies',
		'count': 'Counts'
	};
	return typeMap[type.toLowerCase()] || type;
};

/**
 * Finds a unit by its ID
 * @param {Array} units - Array of unit objects
 * @param {string} unitId - Unit ID to find
 * @returns {Object|null} Unit object or null if not found
 */
export const findUnitById = (units, unitId) => {
	if (!units || !Array.isArray(units) || !unitId) {
		return null;
	}

	return units.find((unit) => { return unit.id === unitId; }) || null;
};

/**
 * Converts a value from display unit to base unit
 * @param {number} value - Value in display unit
 * @param {Object} unit - Unit object with multiplicationFactor
 * @returns {number} Value in base unit
 */
export const convertToBaseUnit = (value, unit) => {
	if (!unit || !unit.multiplicationFactor) {
		return value;
	}

	return value * unit.multiplicationFactor;
};

/**
 * Converts a value from base unit to display unit
 * @param {number} value - Value in base unit
 * @param {Object} unit - Unit object with multiplicationFactor
 * @returns {number} Value in display unit
 */
export const convertFromBaseUnit = (value, unit) => {
	if (!unit || !unit.multiplicationFactor) {
		return value;
	}

	return value / unit.multiplicationFactor;
};
